import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import { useUserRole } from '@/hooks/useUserRole'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useYardiGuestSearch } from '../hooks/useYardiGuestSearch'
import { YARDI_GUEST_SEARCH_CRITERIA_MESSAGE, YARDI_STATUS_MAPPING } from '../constants'
import type { YardiGuestCardRow, FailedReasonCellProps, GridRowProps } from '../types'
import {
  maskformatPhoneNumber,
  formatLrCheckErrorForDisplay,
  sanitizeDataTestIdSegment,
} from '../utils/yardiInviteUi'
import { cn } from '@/lib/utils/helpers'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import yardiTableStyles from '../styles/yardiListing.module.css'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ValidationModal } from '@/components/ui/ValidationModal'
import {
  TableSearchFilterEmptyPanel,
} from '@/components/ui/TableSearchFilterEmptyState'
import { YardiGuestSearchForm } from '../components/YardiGuestSearchForm'

function formatShortDate(v: string | null | undefined): string {
  if (!v) return ''
  try {
    const d = parseISO(String(v).replace(' ', 'T'))
    if (isValid(d)) return format(d, 'MM/dd/yyyy')
    const d2 = new Date(v)
    if (isValid(d2)) return format(d2, 'MM/dd/yyyy')
  } catch {
    /* ignore */
  }
  return String(v)
}

function guestName(inv: YardiGuestCardRow): string {
  return [
    inv.namePrefix,
    inv.firstName,
    inv.middleName,
    inv.lastName,
  ]
    .filter(Boolean)
    .join(' ')
}

function FailedReasonCell({
  invite,
  isLastRow,
  rowKey,
}: FailedReasonCellProps) {
  if (invite.state !== 'FAILED' || !invite.lr_check_error) return null

  return (
    <span
      className="inline-flex items-center gap-1 align-middle"
      data-testid={`yardi-guest-search-failure-reason-cell-${rowKey}`}
    >
      <InfoPopover
        content={formatLrCheckErrorForDisplay(invite.lr_check_error)}
        align="end"
        maxWidth={320}
        isLastRow={isLastRow}
        testId={`yardi-guest-search-failure-details-popover-${rowKey}`}
        className="text-slate-800"
      >
        <button
          type="button"
          data-testid={`yardi-guest-search-failure-details-button-${rowKey}`}
          className={cn(
            'inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0.5 align-middle',
            'text-sm leading-none text-green-600 hover:text-green-700',
            'focus-visible:rounded-sm focus-visible:outline focus-visible:ring-2 focus-visible:ring-green-600/45 focus-visible:ring-offset-1',
          )}
          aria-label="Show application failure details"
          title="Show failure details"
        >
          <i className="fa fa-info-circle" style={{ color: 'green' }} aria-hidden />
        </button>
      </InfoPopover>
    </span>
  )
}

export function YardiGuestSearchPage() {
  const role = useUserRole()
  const isMobile = useIsMobile()
  const isListView = !isMobile

  const guest = useYardiGuestSearch({
    roleName: role.roleName,
    orgId: role.orgId != null ? String(role.orgId) : null,
    companyId: role.companyId != null ? String(role.companyId) : null,
    propertyId: role.propertyId != null ? String(role.propertyId) : null,
    orgName: role.orgName != null ? String(role.orgName) : null,
    companyName: role.companyName != null ? String(role.companyName) : null,
    propertyName: role.propertyName != null ? String(role.propertyName) : null,
  })

  return (
    <div
      className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900"
      data-testid="yardi-guest-search-page"
      style={{ padding: 0 }}
    >
      <PageHeader title="SEARCH YARDI GUEST CARDS" testIdRoot="yardi-guest-search-page-header" />

      <div
        className="mx-auto flex w-full min-w-0 flex-col gap-3 px-3 py-4 sm:gap-4 sm:px-6 sm:py-6"
        data-testid="yardi-guest-search-main"
      >
        <div className="flex flex-wrap items-center justify-end gap-3" data-testid="yardi-guest-search-toolbar-actions">
          <Link
            to="/yardiinvite"
            data-testid="yardi-guest-search-invite-list-link"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#0d7acc] hover:shadow-xl md:flex-none whitespace-nowrap"
          >
            Yardi Invite List
          </Link>
          <Link
            to="/yardiinvite/add"
            data-testid="yardi-guest-search-add-invite-link"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#0d7acc] hover:shadow-xl md:flex-none whitespace-nowrap"
          >
            Add New Invite
          </Link>
        </div>

        <YardiGuestSearchForm guest={guest} />

        <ValidationModal
          isOpen={guest.guestSearchCriteriaModalOpen}
          title="Validation Message"
          message={YARDI_GUEST_SEARCH_CRITERIA_MESSAGE}
          alignMessage
          showValidationIcon
          onClose={guest.dismissGuestSearchCriteriaModal}
          overlayDataTestId="yardi-guest-search-criteria-validation-modal"
          closeButtonDataTestId="yardi-guest-search-criteria-validation-close"
          confirmButtonDataTestId="yardi-guest-search-criteria-validation-ok"
        />

        {guest.resultsLoading ? (
          <div
            data-testid="yardi-guest-search-loading"
            className="flex min-h-[280px] items-center justify-center rounded-xl border border-slate-200 bg-white py-20 shadow-sm"
          >
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading" />
          </div>
        ) : guest.invites && guest.invites.length > 0 ? (
          isListView ? (
            <div
              data-testid="yardi-guest-search-results-card"
              className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              <div className={yardiTableStyles.tableCard} data-testid="yardi-guest-search-table-card">
                <div
                  className={`${yardiTableStyles.tableResponsive} touch-scroll`}
                  data-testid="yardi-guest-search-table-scroll"
                >
                  <table
                    className={cn(
                      yardiTableStyles.listingTable,
                      yardiTableStyles.yardiListingTable,
                      'w-full min-w-0 max-w-full table-fixed',
                    )}
                    data-testid="yardi-guest-search-table"
                  >
                    <thead data-testid="yardi-guest-search-table-head">
                      <tr>
                        <th data-testid="yardi-guest-search-table-col-prospect-id">Prospect ID</th>
                        <th data-testid="yardi-guest-search-table-col-tenant-id">Tenant ID</th>
                        <th data-testid="yardi-guest-search-table-col-name">Name</th>
                        <th data-testid="yardi-guest-search-table-col-email">Email</th>
                        <th data-testid="yardi-guest-search-table-col-phone">Phone</th>
                        <th data-testid="yardi-guest-search-table-col-credit-check">Credit Check Status</th>
                        <th data-testid="yardi-guest-search-table-col-lease-start">Lease Start Date</th>
                        <th data-testid="yardi-guest-search-table-col-target-move-in">Target Move In Date</th>
                        <th data-testid="yardi-guest-search-table-col-expected-move-in">Expected Move In Date</th>
                        <th data-testid="yardi-guest-search-table-col-relationship">Relationship Type</th>
                        <th className="!text-center" data-testid="yardi-guest-search-table-col-manual">
                          Manual
                        </th>
                        <th className="!text-center" data-testid="yardi-guest-search-table-col-automated">
                          Automated
                        </th>
                        <th className="!text-center" data-testid="yardi-guest-search-table-col-status">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody data-testid="yardi-guest-search-table-body">
                      {guest.invites.map((invite, idx) => {
                        const isLast = idx === guest.invites!.length - 1
                        const rowKey = `${sanitizeDataTestIdSegment(String(invite.prospectId ?? 'row'))}-${idx}`
                        return (
                          <tr key={`${invite.prospectId}-${idx}`} data-testid={`yardi-guest-search-table-row-${rowKey}`}>
                            <td
                              className="min-w-0 align-top"
                              data-testid={`yardi-guest-search-cell-prospect-id-${rowKey}`}
                            >
                              {invite.prospectId}
                            </td>
                            <td
                              className="min-w-0 align-top"
                              data-testid={`yardi-guest-search-cell-tenant-id-${rowKey}`}
                            >
                              {invite.tenantId}
                            </td>
                            <td data-testid={`yardi-guest-search-cell-name-${rowKey}`}>{guestName(invite)}</td>
                            <td data-testid={`yardi-guest-search-cell-email-${rowKey}`}>{invite.email}</td>
                            <td data-testid={`yardi-guest-search-cell-phone-${rowKey}`}>
                              {invite.mobileNo ? maskformatPhoneNumber(String(invite.mobileNo)) : ''}
                            </td>
                            <td data-testid={`yardi-guest-search-cell-credit-check-${rowKey}`}>
                              {invite.creditCheckStatus}
                            </td>
                            <td data-testid={`yardi-guest-search-cell-lease-start-${rowKey}`}>
                              {formatShortDate(invite.leaseStartDate)}
                            </td>
                            <td data-testid={`yardi-guest-search-cell-target-move-in-${rowKey}`}>
                              {formatShortDate(invite.targetMoveInDate)}
                            </td>
                            <td data-testid={`yardi-guest-search-cell-expected-move-in-${rowKey}`}>
                              {formatShortDate(invite.expectedMoveInDate)}
                            </td>
                            <td data-testid={`yardi-guest-search-cell-relationship-${rowKey}`}>{invite.type}</td>
                            <td className="!text-center" data-testid={`yardi-guest-search-cell-manual-${rowKey}`}>
                              {invite.isManualInvite ? 'Yes' : 'No'}
                            </td>
                            <td className="!text-center" data-testid={`yardi-guest-search-cell-automated-${rowKey}`}>
                              {invite.isAutomatedInvite ? 'Yes' : 'No'}
                            </td>
                            <td
                              className="min-w-0 !text-center align-top"
                              data-testid={`yardi-guest-search-cell-status-${rowKey}`}
                            >
                              {invite.state && (
                                <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-1 break-words">
                                  {YARDI_STATUS_MAPPING[invite.state] || invite.state}
                                  <FailedReasonCell
                                    invite={invite}
                                    isLastRow={isLast}
                                    rowKey={rowKey}
                                  />
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div
              data-testid="yardi-guest-search-mobile-layout"
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {guest.invites.map((invite, idx) => {
                const cardKey = `${sanitizeDataTestIdSegment(String(invite.prospectId ?? 'row'))}-${idx}`
                return (
                  <div
                    key={`${invite.prospectId}-${idx}`}
                    data-testid={`yardi-guest-search-mobile-card-${cardKey}`}
                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <GridRow
                      label="Prospect ID"
                      value={invite.prospectId}
                      fieldTestId={`yardi-guest-search-mobile-field-prospect-id-${cardKey}`}
                    />
                    <GridRow
                      label="Tenant ID"
                      value={invite.tenantId}
                      fieldTestId={`yardi-guest-search-mobile-field-tenant-id-${cardKey}`}
                    />
                    <GridRow
                      label="Name"
                      value={guestName(invite)}
                      fieldTestId={`yardi-guest-search-mobile-field-name-${cardKey}`}
                    />
                    <GridRow
                      label="Email"
                      value={invite.email}
                      fieldTestId={`yardi-guest-search-mobile-field-email-${cardKey}`}
                    />
                    <GridRow
                      label="Phone"
                      value={invite.mobileNo ? maskformatPhoneNumber(String(invite.mobileNo)) : ''}
                      fieldTestId={`yardi-guest-search-mobile-field-phone-${cardKey}`}
                    />
                    <GridRow
                      label="Credit Check Status"
                      value={invite.creditCheckStatus}
                      fieldTestId={`yardi-guest-search-mobile-field-credit-check-${cardKey}`}
                    />
                    <GridRow
                      label="Lease Start Date"
                      value={formatShortDate(invite.leaseStartDate)}
                      fieldTestId={`yardi-guest-search-mobile-field-lease-start-${cardKey}`}
                    />
                    <GridRow
                      label="Target Move In Date"
                      value={formatShortDate(invite.targetMoveInDate)}
                      fieldTestId={`yardi-guest-search-mobile-field-target-move-in-${cardKey}`}
                    />
                    <GridRow
                      label="Expected Move In Date"
                      value={formatShortDate(invite.expectedMoveInDate)}
                      fieldTestId={`yardi-guest-search-mobile-field-expected-move-in-${cardKey}`}
                    />
                    <GridRow
                      label="Relationship Type"
                      value={invite.type}
                      fieldTestId={`yardi-guest-search-mobile-field-relationship-${cardKey}`}
                    />
                    <GridRow
                      label="Manual"
                      value={invite.isManualInvite ? 'Yes' : 'No'}
                      fieldTestId={`yardi-guest-search-mobile-field-manual-${cardKey}`}
                    />
                    <GridRow
                      label="Automated"
                      value={invite.isAutomatedInvite ? 'Yes' : 'No'}
                      fieldTestId={`yardi-guest-search-mobile-field-automated-${cardKey}`}
                    />
                    <GridRow
                      label="Status"
                      value={YARDI_STATUS_MAPPING[invite.state ?? ''] || invite.state || ''}
                      fieldTestId={`yardi-guest-search-mobile-field-status-${cardKey}`}
                    />
                    {invite.state === 'FAILED' && invite.lr_check_error != null && (
                      <div
                        className="min-w-0"
                        data-testid={`yardi-guest-search-mobile-field-failure-reason-${cardKey}`}
                      >
                        <p className="mt-1.5 text-xs font-semibold text-slate-500">Application Failure Reason</p>
                        <p className="break-words text-sm">
                          {formatLrCheckErrorForDisplay(invite.lr_check_error)}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        ) : (
          <TableSearchFilterEmptyPanel
            data-testid={
              isListView ? 'yardi-guest-search-table-empty-message' : 'yardi-guest-search-mobile-empty'
            }
          />
        )}
      </div>
    </div>
  )
}

function GridRow({
  label,
  value,
  fieldTestId,
}: GridRowProps) {
  return (
    <div className="mb-1.5" data-testid={fieldTestId}>
      <p className="text-xs font-semibold leading-tight text-slate-500">{label}</p>
      <p className="text-sm leading-snug text-slate-800">{value ?? ''}</p>
    </div>
  )
}
